// { rules, pair, tilt, width, height, material, allAccessories, selectedAccessories, allPackets, selectedPackets }
class RulesService {
    constructor(jsonService) {
        this.jsonService = jsonService;
        this.accessoryTypes = {
            fixInFix: 2,
            rollerShutter: 3,
            blind: 4,
            cutForWindowSill: 5,
            slotVentilator: 6,
            hiddenHinges: 7,
            lowThreshold: 13,
            other: 25,
        };

        this.accessoryRules = {
            2: this.fixInFixRules.bind(this),
            3: this.rollerShutterRules.bind(this),
            4: this.blindRules.bind(this),
            5: this.cutForWindowSillRules.bind(this),
            6: this.slotVentiltorRules.bind(this),
            7: this.hiddenHingesRules.bind(this),
            8: this.visibleHingesRules.bind(this),
            10: this.brakeInHandleRules.bind(this),
            11: this.selfClosingSystemRules.bind(this),
            12: this.frictionBrakeRules.bind(this),
            13: this.lowThresholdRules.bind(this),
            25: this.otherAccessoriesRules.bind(this),
        };

        this.valgeDoorBrakeRuleAccessoryIds = [800, 801];
    }

    fixInFixRules({ rules, min_sizes, width, height }) {
        if (rules.type < 2) {
            return true;
        }
        if (!rules.fix && !rules.turn_tilt) {
            return true;
        }
        return min_sizes &&
            min_sizes.hidden.height <= height &&
            min_sizes.hidden.width <= width;
    }

    rollerShutterRules({ rules, width, selectedAccessories }) {
        if (rules.type < 2) {
            return true;
        }
        return width >= 400 &&
            !this.hasAccessoryType(selectedAccessories, this.accessoryTypes.slotVentilator);
    }

    blindRules({ rules, width, height, material, selectedAccessories }) {
        if (rules.type < 2) {
            return true;
        }
        return ((material === 'pvc' && width >= 450) || width >= 250) &&
            width <= 2200 && width * height <= 2500000 &&
            (!this.hasAccessoryType(selectedAccessories, this.accessoryTypes.slotVentilator) ||
                rules.window);
    }

    cutForWindowSillRules({ rules, selectedAccessories }) {
        if (rules.type < 2) {
            return true;
        }
        return !this.hasAccessoryType(selectedAccessories, this.accessoryTypes.lowThreshold);
    }

    slotVentiltorRules({ rules, width, tilt, selectedAccessories }) {
        if (rules.type < 0) {
            return true;
        }
        const result = !tilt && (!rules.window || width >= 501);

        if (rules.type < 2) {
            return result;
        }
        return result &&
            !this.hasAccessoryType(selectedAccessories, this.accessoryTypes.rollerShutter) &&
            (!this.hasAccessoryType(selectedAccessories, this.accessoryTypes.blind) || rules.window);
    }

    hiddenHingesRules({ rules, min_sizes, pair, width, height, selectedAccessories }) {
        if (rules.type < 2) {
            return true;
        }

        if (this.balconyDoorsRuleForHiddenHinges(rules, selectedAccessories)) {
            return false;
        }

        return this.dimensionsRuleForHiddenHinges(rules, min_sizes, pair, width, height);
    }

    balconyDoorsRuleForHiddenHinges(rules, selectedAccessories) {
        return (
            this.hasAccessoryType(selectedAccessories, this.accessoryTypes.lowThreshold) &&
            rules.eligible_for_balcony_doors_rule
        );
    }

    dimensionsRuleForHiddenHinges(rules, min_sizes, pair, width, height) {
        if (rules.window_door || rules.fix || rules.turn_tilt) {
            if (rules.window_door && rules.openable_outwards) {
                if (pair) {
                    return width >= 1100;
                }

                return width >= 600;
            }

            if (min_sizes) {
                return min_sizes &&
                    min_sizes.hidden.height <= height &&
                    min_sizes.hidden.width <= width;
            }
        }

        return true;
    }

    visibleHingesRules({ rules, min_sizes, pair, width, height }) {
        if (rules.type < 2) {
            return true;
        }

        if (rules.window_door || rules.fix || rules.turn_tilt) {
            if (rules.window_door && rules.openable_outwards) {
                if (pair) {
                    return width >= 1150;
                }

                return width >= 590;
            }

            return min_sizes &&
                min_sizes.visible.height <= height &&
                min_sizes.visible.width <= width;
        }

        return true;
    }

    brakeInHandleRules({ rules, pair, width, selectedAccessories }) {
        if (rules.type < 2) {
            return true;
        }

        if (rules.window_door && rules.openable_outwards) {
            if (pair) {
                if (this.hasAccessoryType(selectedAccessories, this.accessoryTypes.hiddenHinges)) {
                    return width >= 1890;
                }
                return width >= 1290;
            }

            if (this.hasAccessoryType(selectedAccessories, this.accessoryTypes.hiddenHinges)) {
                return width >= 930;
            }
            return width >= 680;
        }

        return true;
    }

    selfClosingSystemRules({ rules, width }) {
        if (rules.type < 0) {
            return true;
        }

        return !rules.psk || width >= 1801;
    }

    frictionBrakeRules({ rules, material, pair, width }) {
        if (rules.type < 2 || material !== 'pvc' || !rules.openable_outwards) {
            return true;
        }

        return pair ? width >= 1500 : width >= 750;
    }

    lowThresholdRules({ rules, selectedAccessories }) {
        if (rules.type < 2) {
            return true;
        }

        if (this.balconyDoorsRuleForLowThreshold(rules, selectedAccessories)) {
            return false;
        }

        return !this.hasAccessoryType(selectedAccessories, this.accessoryTypes.cutForWindowSill);
    }

    balconyDoorsRuleForLowThreshold(rules, selectedAccessories) {
        return (
            this.hasAccessoryType(selectedAccessories, this.accessoryTypes.hiddenHinges) &&
            rules.eligible_for_balcony_doors_rule
        );
    }

    otherAccessoriesRules({ rules, width, selectedAccessories }) {
        return this.valgeDoorBrakeRule(rules, width, selectedAccessories);
    }

    valgeDoorBrakeRule( rules, width, selectedAccessories) {
        if (rules.eligible_for_valge_doors_rule &&
            selectedAccessories.find(accessory => this.valgeDoorBrakeRuleAccessoryIds.includes(accessory.id))
        ) {
            return width >= 770;
        }

        return true;
    }

    dividerRequired({ rules, material, pair, width }) {
        if (rules.window_door && material === 'pvc' && !this.hasPanel()) {
            if (pair) {
                return width > 2000;
            }
            return width > 1000;
        }
        return false;
    }

    validateSelectedAccessories(options) {
        const { selectedAccessories } = options;
        return selectedAccessories.every((selected) => {
            if (this.hasAccessoryType(
                    selectedAccessories, selected.attributes.accessoryType, selected.id
                )) {
                if (!selected.attributes.allowMultiple && !selected.attributes.isDefault && selected.attributes.price !== 0) {
                    return false;
                }
            }
            if (this.accessoryRules[selected.attributes.accessoryType]) {
                return this.accessoryRules[selected.attributes.accessoryType](options);
            }
            return true;
        });
    }

    validateSelectedPackets({ selectedPackets }) {
        return selectedPackets.every(
            selected => (selected.attributes.price == null ||
                selected.attributes.price > 0 ||
                selected.id <= 0
            ) && !this.hasPacketType(selectedPackets, selected.attributes.packetType, selected.id)
        );
    }

    validateAccessories(options) {
        const { allAccessories, allPackets } = options;
        let { selectedAccessories, selectedPackets } = options;

        const result = {
            dividerRequired: this.dividerRequired(options),
            accessories: {},
            packets: {}
        };
        const hiddenHinge = selectedAccessories.find(
            item => Number(item.attributes.accessoryType) === this.accessoryTypes.hiddenHinges
        );
        const validHinge = hiddenHinge && this.validateSelectedAccessories({
            ...options,
            selectedAccessories: [hiddenHinge]
        });
        if (hiddenHinge) {
            result.accessories[hiddenHinge.id] = validHinge;
        }

        selectedAccessories = selectedAccessories.filter((accessory) => {
            const accessoryOptions = {
                ...options,
                selectedAccessories: [accessory]
            };
            if (validHinge) {
                accessoryOptions.selectedAccessories.push(hiddenHinge);
            }
            result.accessories[accessory.id] = result.accessories[accessory.id] !== false &&
                this.validateSelectedAccessories(accessoryOptions);
            return result.accessories[accessory.id];
        });

        selectedPackets = selectedPackets.filter((packet) => {
            const accessoryOptions = {
                ...options,
                selectedAccessories: this.getPacketAccessories(packet, allAccessories)
            };
            if (validHinge) {
                accessoryOptions.selectedAccessories.push(hiddenHinge);
            }
            const packetOptions = {
                ...options,
                selectedPackets: [...selectedPackets, packet]
            };
            result.packets[packet.id] = this.validateSelectedAccessories(accessoryOptions) &&
                this.validateSelectedPackets(packetOptions);
            return result.packets[packet.id];
        });

        allAccessories.forEach((accessory) => {
            const accessoryOptions = {
                ...options,
                selectedAccessories: [...selectedAccessories, accessory]
            };
            result.accessories[accessory.id] = result.accessories[accessory.id] !== false &&
                this.validateSelectedAccessories(accessoryOptions);
        });

        allPackets.forEach((packet) => {
            const accessoryOptions = {
                ...options,
                selectedAccessories: [
                    ...selectedAccessories,
                    ...this.getPacketAccessories(packet, allAccessories)
                ]
            };
            const packetOptions = {
                ...options,
                selectedPackets: [...selectedPackets, packet]
            };
            result.packets[packet.id] = result.packets[packet.id] !== false &&
                this.validateSelectedAccessories(accessoryOptions) &&
                this.validateSelectedPackets(packetOptions);
        });

        return result;
    }

    hasPanel() {
        return this.jsonService.jsonObject.sashes.some(sash =>
            sash.filling.type === 'pvc_panels' ||
            sash.intSashes.some(intSash => intSash.filling.type === 'pvc_panels')
        );
    }

    hasAccessoryType(selectedAccessories, type, id) {
        return selectedAccessories.some(
            acc => Number(acc.id) !== Number(id) &&
            Number(acc.attributes.accessoryType) === Number(type)
        );
    }

    hasPacketType(selectedPackets, type, id) {
        return selectedPackets.some(
            packet => Number(packet.id) !== Number(id) &&
            Number(packet.attributes.packetType) === Number(type)
        );
    }

    getPacketAccessories(packet, allAccessories) {
        if (!packet.relationships ||
            !packet.relationships.accessories ||
            !packet.relationships.accessories.data
        ) {
            return [];
        }
        return packet.relationships.accessories.data.map(
            packetAccessory => allAccessories.find(acc => Number(acc.id) === Number(packetAccessory.id))
        ).filter(Boolean);
    }
}

RulesService.$inject = [
    'jsonService'
];

export default angular.module('rulesService', [])
    .service('rulesService', RulesService)
    .name;
